import SkeletonRow from '@/components/UI/molecules/tables/SkeletonRow';
import {
    TableCell,
    TableCellHead,
    TableCellHeadInitial,
    TableContainerBinnance,
    TableHeadRow,
} from '@/components/UI/molecules/tables/TablePrincipal';
import StatusTextGenecic from '@/components/UI/atoms/buttons/StatusTextGeneric';
import {Can} from '@/context/AbilityContext';
import {ExpenseAccountValidationEnum, FeatureCodeEnum} from '@/enums/permissions.enum';

import {ACLObj} from '@components/auth/guard/AclGuard';
import {TextButton, TextButtonDetails} from '@components/UI/molecules/textButton/textButtons';
import CustomPagination from '@components/UI/organisms/pagination/Pagination';
import {AppDispatch, RootState} from '@config/store';
import {yupResolver} from '@hookform/resolvers/yup';
import {
    Box,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import Badge from '@mui/material/Badge';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {enUS, es} from 'date-fns/locale';
import {DateTime} from 'luxon';
import React, {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import * as yup from 'yup';

import ModalExpenseAccountNotes from '@components/expenseAccountValidation/modals/ModalExpenseAccountNotes';
import ModalApproveExpenseAccount from '@components/expenseAccountValidation/modals/ModalApproveExpenseAccount';
import ModalRejectExpenseAccount from '@components/expenseAccountValidation/modals/ModalRejectExpenseAccount';
import {
    approveExpenseAccount,
    rejectExpenseAccount,
    downloadExpenseAccountZip,
    fetchGetExpenseAccountsValidation,
    setExpenseAccountsValidationFilters,
} from '@components/expenseAccountValidation/redux/actions/ExpenseAccountValidationActions';
import {
    IExpenseAccountValidationFilters,
    IExpenseAccountValidationRow,
} from '@components/expenseAccountValidation/redux/types/ExpenseAccountValidationTypes';

/**
 * Opciones del desplegable de estado (criterio 12).
 *
 * `PROCESSED` no es un estado de la Cuenta de Gastos: es una aprobada cuyo envío a SAP ya
 * quedó registrado. El criterio 13 pide poder consultarlas, y el backend lo resuelve como
 * valor de filtro para no tocar el vocabulario de estados.
 */
const STATUSES = ['PENDING_VALIDATION', 'REJECTED', 'APPROVED', 'PROCESSED'];

/**
 * Años disponibles en el filtro de ejercicio fiscal.
 *
 * Lista fija por ahora, a pedido del negocio. Cuando haya que agregar 2027 es una línea;
 * si se vuelve incómodo, se calcula a partir del año en curso.
 */
const FISCAL_YEARS = ['2024', '2025', '2026'];

const formatDate = (value: string | null): string =>
    value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy') : '---';

const formatDateTime = (value: string | null): string =>
    value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy HH:mm') : '---';

/**
 * Módulo interno "Validación de Cuenta de Gastos" (HU 2, criterios 1, 2, 4, 6, 12 y 13).
 *
 * Muestra **una fila por Cuenta de Gastos**, la vigente. Cuando el Agente Aduanal recarga
 * tras un rechazo, el renglón se actualiza en lugar de duplicarse: el listado que devuelve
 * el backend ya viene resuelto a la versión más alta.
 *
 * Las acciones de aprobar y rechazar todavía NO están aquí: sus modales dependen del
 * catálogo de motivos y de si el rechazo puede señalar una factura concreta, dos cosas
 * pendientes de definición. Se agregan en la segunda pasada, junto con el endpoint.
 */
const ExpenseAccountValidation: React.FC & {acl?: ACLObj} = () => {
    const userLang = localStorage.getItem('user-lang') || 'es';
    const lang = userLang === 'es' ? es : enUS;
    const {t} = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();

    const token = useSelector((state: RootState) => state.logins.accessToken);
    const expenseAccounts = useSelector(
        (state: RootState) => state.expenseAccountValidation.expenseAccounts
    );
    const currentPage = useSelector((state: RootState) => state.expenseAccountValidation.page);
    const limit = useSelector((state: RootState) => state.expenseAccountValidation.limit);
    const totalPages = useSelector((state: RootState) => state.expenseAccountValidation.totalPages);
    const loading = useSelector((state: RootState) => state.expenseAccountValidation.loading);
    const filters = useSelector((state: RootState) => state.expenseAccountValidation.filters);

    /** Cuenta de Gastos cuyo modal de notas está abierto. `null` = cerrado. */
    const [notesOf, setNotesOf] = useState<IExpenseAccountValidationRow | null>(null);
    const [downloading, setDownloading] = useState<string | null>(null);
    /** Cuenta de Gastos cuyo modal de aprobación está abierto. `null` = cerrado. */
    const [approving, setApproving] = useState<IExpenseAccountValidationRow | null>(null);
    /** Cuenta de Gastos cuyo modal de rechazo está abierto. `null` = cerrado. */
    const [rejecting, setRejecting] = useState<IExpenseAccountValidationRow | null>(null);

    const schema = yup.object({
        uploadedFrom: yup.date().nullable(),
        uploadedTo: yup.date().nullable(),
        society: yup.string(),
        customAgent: yup.string(),
        folio: yup.string(),
        status: yup.string(),
        year: yup.string(),
    });
    const {handleSubmit, control, reset, watch} = useForm<IExpenseAccountValidationFilters>({
        resolver: yupResolver(schema),
    });
    // Para que cada fecha acote a la otra y no se pueda elegir un rango invertido.
    const watchForm = watch();

    useEffect(() => {
        if (token) {
            // PDPA-5, criterio 8 · Se reaplican los filtros guardados: al volver del
            // detalle el revisor recupera su búsqueda en lugar del listado completo.
            dispatch(fetchGetExpenseAccountsValidation(token, currentPage, limit, filters));
        }
        // Solo al montar: los cambios de página y de filtro los despachan sus propios
        // manejadores, y depender de ellos aquí dispararía una consulta de más por cada uno.
    }, [dispatch, token]);

    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
        dispatch(fetchGetExpenseAccountsValidation(token, value, limit, filters));
    };

    const handleChangeRowsPerPage = (event: any) => {
        dispatch(fetchGetExpenseAccountsValidation(token, 1, event.target.value, filters));
    };

    const applyFilters = (data: IExpenseAccountValidationFilters) => {
        dispatch(setExpenseAccountsValidationFilters(data));
        // Siempre vuelve a la página 1: quedarse en la 3 con un filtro nuevo suele dejar
        // la tabla vacía y parece que no hay resultados.
        dispatch(fetchGetExpenseAccountsValidation(token, 1, limit, data));
    };

    const clearFilters = () => {
        reset({});
        dispatch(setExpenseAccountsValidationFilters({}));
        dispatch(fetchGetExpenseAccountsValidation(token, 1, limit, {}));
    };

    /**
     * Criterio 3 · Detalle dentro de este módulo, con su propia ruta y su propio permiso.
     *
     * Antes navegaba a la ruta de Agentes Aduanales, y eso obligaba a que el usuario
     * interno tuviera además el permiso de ese módulo para poder consultar el detalle.
     */
    const goToDetail = (row: IExpenseAccountValidationRow) => {
        navigate(
            `/expense-accounts-validation/${row.customAgent}/custom-request/${row.customRequest}`
        );
    };

    /**
     * Criterio 5. Al aprobar se libera el envío a SAP, así que se recarga el listado: la
     * fila pasa a "Procesada" y sus acciones de decisión desaparecen.
     */
    const confirmApproval = async (comments: string): Promise<string | void> => {
        if (!approving) return;
        try {
            await approveExpenseAccount(
                token,
                approving.customAgent,
                approving.customRequest,
                comments
            );
            dispatch(fetchGetExpenseAccountsValidation(token, currentPage, limit, filters));
        } catch (error) {
            // El envío a SAP puede fallar DESPUÉS de que la aprobación quedó firme, así que
            // se recarga igual: el estado pudo cambiar aunque la respuesta sea un error.
            dispatch(fetchGetExpenseAccountsValidation(token, currentPage, limit, filters));
            return t('expense_account_validation.approve.error');
        }
    };

    /** Criterios 8 y 9. No manda nada a SAP: solo deja el expediente rechazado. */
    const confirmRejection = async (payload: {
        reason: string;
        comments: string;
        invoiceUuid: string;
    }): Promise<string | void> => {
        if (!rejecting) return;
        try {
            await rejectExpenseAccount(
                token,
                rejecting.customAgent,
                rejecting.customRequest,
                payload
            );
            dispatch(fetchGetExpenseAccountsValidation(token, currentPage, limit, filters));
        } catch (error) {
            return t('expense_account_validation.reject.error');
        }
    };

    const downloadZip = async (row: IExpenseAccountValidationRow) => {
        setDownloading(row.expenseAccountId);
        try {
            await downloadExpenseAccountZip(token, row.customAgent, row.customRequest);
        } catch (error) {
            // El backend distingue "no hay expediente guardado" de un fallo real; ambos
            // llegan aquí como error de descarga porque el usuario no puede hacer nada
            // distinto en uno u otro caso.
            window.alert(t('expense_account_validation.zip.download_error'));
        } finally {
            setDownloading(null);
        }
    };

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%', padding: '15px 23px'}}>
            <Typography
                sx={{
                    fontSize: '24px',
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 400,
                    padding: '10px 0',
                }}
            >
                {t('expense_account_validation.title')}
            </Typography>

            <Box component="form" onSubmit={handleSubmit(applyFilters)}>
                <Can
                    I={ExpenseAccountValidationEnum.FILTER}
                    a={FeatureCodeEnum.EXPENSE_ACCOUNT_VALIDATION}
                >
                    {/*
                      Mismo armado que la barra de filtros de Agentes Aduanales: dos franjas
                      sobre fondo blanco, la primera con los campos de texto y la segunda con
                      las fechas, el ejercicio y las acciones. Se replica en vez de extraer un
                      componente común porque ese módulo es de otra HU y tocarlo para
                      compartir estructura obligaría a reprobarlo entero.
                    */}
                    <Box
                        sx={{
                            display: 'flex',
                            padding: '15px',
                            paddingBottom: '0',
                            background: '#fff',
                            gap: '10px',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Controller
                            name="society"
                            control={control}
                            render={({field: {onChange, value}}) => (
                                <TextField
                                    value={value ?? ''}
                                    onChange={onChange}
                                    label={t('expense_account_validation.filters.society')}
                                    size="small"
                                />
                            )}
                        />
                        <Controller
                            name="customAgent"
                            control={control}
                            render={({field: {onChange, value}}) => (
                                <TextField
                                    value={value ?? ''}
                                    onChange={onChange}
                                    label={t('expense_account_validation.filters.custom_agent')}
                                    size="small"
                                />
                            )}
                        />
                        <Controller
                            name="folio"
                            control={control}
                            render={({field: {onChange, value}}) => (
                                <TextField
                                    value={value ?? ''}
                                    onChange={onChange}
                                    label={t('expense_account_validation.filters.folio')}
                                    size="small"
                                />
                            )}
                        />
                        <Controller
                            name="status"
                            control={control}
                            render={({field}) => (
                                <FormControl size="small" sx={{minWidth: '190px'}}>
                                    {/* `shrink` + `notched`: la etiqueta se queda arriba
                                        aunque el filtro esté vacío, como se ve en Agentes
                                        Aduanales, donde sube porque siempre trae valor. */}
                                    <InputLabel shrink>
                                        {t('expense_account_validation.filters.status')}
                                    </InputLabel>
                                    <Select
                                        {...field}
                                        notched
                                        displayEmpty
                                        value={field.value ?? ''}
                                        label={t('expense_account_validation.filters.status')}
                                    >
                                        <MenuItem value="">
                                            {t('expense_account_validation.filters.all')}
                                        </MenuItem>
                                        {STATUSES.map((status) => (
                                            <MenuItem key={status} value={status}>
                                                {t(`expense_account_validation.status.${status}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            padding: '15px',
                            paddingTop: '15px',
                            background: '#fff',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '10px',
                        }}
                    >
                        <Box sx={{display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap'}}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} locale={lang}>
                                <Controller
                                    name="uploadedFrom"
                                    control={control}
                                    render={({field: {onChange, value}}) => (
                                        <DatePicker
                                            label={t('logbook_and_details.start_date')}
                                            value={value ?? null}
                                            onChange={onChange}
                                            maxDate={watchForm.uploadedTo}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    // Se conserva lo que arma el DatePicker
                                                    // y solo se fuerza que la etiqueta suba.
                                                    InputLabelProps={{
                                                        ...params.InputLabelProps,
                                                        shrink: true,
                                                    }}
                                                    sx={{
                                                        '& .MuiInputBase-root': {
                                                            height: '40px',
                                                            marginRight: '10px',
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDateFns} locale={lang}>
                                <Controller
                                    name="uploadedTo"
                                    control={control}
                                    render={({field: {onChange, value}}) => (
                                        <DatePicker
                                            label={t('logbook_and_details.end_date')}
                                            value={value ?? null}
                                            onChange={onChange}
                                            minDate={watchForm.uploadedFrom}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    // Se conserva lo que arma el DatePicker
                                                    // y solo se fuerza que la etiqueta suba.
                                                    InputLabelProps={{
                                                        ...params.InputLabelProps,
                                                        shrink: true,
                                                    }}
                                                    sx={{
                                                        '& .MuiInputBase-root': {
                                                            height: '40px',
                                                            marginRight: '10px',
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                            <Controller
                                name="year"
                                control={control}
                                render={({field}) => (
                                    <FormControl sx={{marginRight: '10px', height: '40px'}}>
                                        <InputLabel shrink>
                                            {t('expense_account_validation.filters.year')}
                                        </InputLabel>
                                        <Select
                                            {...field}
                                            notched
                                            displayEmpty
                                            value={field.value ?? ''}
                                            label={t('expense_account_validation.filters.year')}
                                            sx={{width: '150px', height: '40px'}}
                                        >
                                            <MenuItem value="">
                                                {t('expense_account_validation.filters.all')}
                                            </MenuItem>
                                            {FISCAL_YEARS.map((year) => (
                                                <MenuItem key={year} value={year}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                            <TextButton type="submit">
                                {t('components.filter_btn.title').toUpperCase()}
                            </TextButton>
                        </Box>
                        <Box sx={{display: 'flex', alignItems: 'flex-end'}}>
                            <TextButtonDetails type="button" onClick={clearFilters}>
                                {t('expense_account_validation.filters.clear').toUpperCase()}
                            </TextButtonDetails>
                        </Box>
                    </Box>
                </Can>
            </Box>

            <TableContainerBinnance container>
                <TableHeadRow container>
                    <Grid item xs={1}>
                        <TableCellHeadInitial sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.table.custom_agent')}
                        </TableCellHeadInitial>
                    </Grid>
                    <Grid item xs={1.6}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.table.custom_request')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={1.4}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.table.folio')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={1}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.table.uploaded_at')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={1.3}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.table.status')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={1.2}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.table.decided_at')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={1.7}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.table.decided_by')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={0.8}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.table.notes')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={2}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.table.actions')}
                        </TableCellHead>
                    </Grid>
                </TableHeadRow>

                {loading ? (
                    <>
                        <SkeletonRow />
                        <SkeletonRow />
                        <SkeletonRow />
                    </>
                ) : (
                    expenseAccounts?.map((row: IExpenseAccountValidationRow) => (
                        <React.Fragment key={row.expenseAccountId}>
                            <Grid item xs={1} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>{row.customAgent}</TableCell>
                            </Grid>
                            <Grid item xs={1.6} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>{row.customRequest}</TableCell>
                            </Grid>
                            <Grid item xs={1.4} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>{row.folio || '---'}</TableCell>
                            </Grid>
                            <Grid item xs={1} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {formatDate(row.uploadedAt)}
                                </TableCell>
                            </Grid>
                            <Grid item xs={1.3} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    <StatusTextGenecic
                                        status={row.status}
                                        text={t(`expense_account_validation.status.${row.status}`)}
                                    />
                                </TableCell>
                            </Grid>
                            <Grid item xs={1.2} display="grid">
                                {/* Criterio 1: vacío mientras está pendiente de validación. */}
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {formatDateTime(row.decidedAt)}
                                </TableCell>
                            </Grid>
                            <Grid item xs={1.7} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.decidedBy ?? '---'}
                                </TableCell>
                            </Grid>
                            <Grid item xs={0.8} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    <Can
                                        I={ExpenseAccountValidationEnum.VIEW_NOTES}
                                        a={FeatureCodeEnum.EXPENSE_ACCOUNT_VALIDATION}
                                    >
                                        {/* Criterio 6: sin notas, la acción va deshabilitada. */}
                                        <Tooltip
                                            title={
                                                row.hasNotes
                                                    ? t('expense_account_validation.notes.see')
                                                    : t('expense_account_validation.notes.empty')
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={!row.hasNotes}
                                                    onClick={() => setNotesOf(row)}
                                                >
                                                    <Badge
                                                        badgeContent={row.notesCount}
                                                        color="primary"
                                                        invisible={!row.hasNotes}
                                                    >
                                                        <ChatBubbleOutlineIcon fontSize="small" />
                                                    </Badge>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Can>
                                </TableCell>
                            </Grid>
                            <Grid item xs={2} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    <Can
                                        I={ExpenseAccountValidationEnum.VIEW_DETAIL}
                                        a={FeatureCodeEnum.EXPENSE_ACCOUNT_VALIDATION}
                                    >
                                        <Tooltip
                                            title={t('expense_account_validation.actions.see')}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => goToDetail(row)}
                                            >
                                                <ArrowForwardIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Can>
                                    <Can
                                        I={ExpenseAccountValidationEnum.APPROVE}
                                        a={FeatureCodeEnum.EXPENSE_ACCOUNT_VALIDATION}
                                    >
                                        {/* Criterio 5: mano hacia arriba, y solo mientras
                                            está pendiente de validación. */}
                                        {row.canApprove && (
                                            <Tooltip
                                                title={t(
                                                    'expense_account_validation.actions.approve'
                                                )}
                                            >
                                                <IconButton
                                                    size="small"
                                                    color="success"
                                                    onClick={() => setApproving(row)}
                                                >
                                                    <ThumbUpOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Can>
                                    <Can
                                        I={ExpenseAccountValidationEnum.REJECT}
                                        a={FeatureCodeEnum.EXPENSE_ACCOUNT_VALIDATION}
                                    >
                                        {/* Criterio 8: mano hacia abajo. */}
                                        {row.canReject && (
                                            <Tooltip
                                                title={t(
                                                    'expense_account_validation.actions.reject'
                                                )}
                                            >
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setRejecting(row)}
                                                >
                                                    <ThumbDownOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Can>
                                    <Can
                                        I={ExpenseAccountValidationEnum.DOWNLOAD_ZIP}
                                        a={FeatureCodeEnum.EXPENSE_ACCOUNT_VALIDATION}
                                    >
                                        <Tooltip
                                            title={t('expense_account_validation.actions.zip')}
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={downloading === row.expenseAccountId}
                                                    onClick={() => downloadZip(row)}
                                                >
                                                    <FolderZipOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Can>
                                </TableCell>
                            </Grid>
                        </React.Fragment>
                    ))
                )}
            </TableContainerBinnance>

            <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                handleChange={handleChange}
                limit={limit}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
            />

            <ModalApproveExpenseAccount
                isModalOpen={Boolean(approving)}
                closeModal={() => setApproving(null)}
                expenseAccount={
                    approving && {folio: approving.folio, customRequest: approving.customRequest}
                }
                onConfirm={confirmApproval}
            />

            <ModalRejectExpenseAccount
                isModalOpen={Boolean(rejecting)}
                closeModal={() => setRejecting(null)}
                expenseAccount={
                    rejecting && {
                        folio: rejecting.folio,
                        customAgent: rejecting.customAgent,
                        customRequest: rejecting.customRequest,
                    }
                }
                onConfirm={confirmRejection}
            />

            <ModalExpenseAccountNotes
                isModalOpen={Boolean(notesOf)}
                closeModal={() => setNotesOf(null)}
                expenseAccount={
                    notesOf && {
                        customAgent: notesOf.customAgent,
                        customRequest: notesOf.customRequest,
                        folio: notesOf.folio,
                    }
                }
            />
        </Box>
    );
};

ExpenseAccountValidation.acl = {
    action: ExpenseAccountValidationEnum.VIEW_LIST,
    subject: FeatureCodeEnum.EXPENSE_ACCOUNT_VALIDATION,
};

export default ExpenseAccountValidation;
